import {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useIndexDB} from '~/common/hooks/useIndexDB';
import {UPDATE_CACHED_IMG} from '~/GalleryPB/reducers/cachedGalleryImg';

const IMG_KEY = 'uploaded-img';

export const useCacheGalleryImg = () => {
  // NOTE: the generic parameter (<string>) is the 'type' that we are storing on IndexedDB
  const [loading, setLoading] = useState(true);
  const {db, add, get, remove, update} = useIndexDB<string>('evite-db', 'gallery-image');
  const galleryImg = useSelector(
    (state: {cachedGalleryImg: {cachedGalleryImg: string}}) =>
      state?.cachedGalleryImg?.cachedGalleryImg || ''
  );
  const hasGalleryImg = galleryImg?.length > 0;

  const dispatch = useDispatch();

  const getCachedImg = useCallback(async () => {
    setLoading(true);
    const cachedImg = await get(IMG_KEY);
    dispatch({type: UPDATE_CACHED_IMG, payload: {cachedGalleryImg: cachedImg?.data || ''}});
    setLoading(false);
  }, [get, dispatch]);

  const storeAndUpdateImg = useCallback(
    async (result: string) => {
      setLoading(true);
      await add(IMG_KEY)(result);
      await getCachedImg();
      setLoading(false);
    },
    [add, getCachedImg]
  );

  const replaceAndUpdateImg = useCallback(
    async (result: string) => {
      setLoading(true);
      await update(IMG_KEY, result);
      await getCachedImg();
      setLoading(false);
    },
    [update, getCachedImg]
  );

  const removeCachedImg = useCallback(async () => {
    setLoading(true);
    await remove(IMG_KEY);
    setLoading(false);
  }, [remove]);

  const removeAndUpdateImg = useCallback(async () => {
    setLoading(true);
    await removeCachedImg();
    await getCachedImg();
    setLoading(false);
  }, [removeCachedImg, getCachedImg]);

  useEffect(() => {
    if (db) {
      getCachedImg();
    }
  }, [db]);

  return {
    galleryImg,
    storeAndUpdateImg,
    removeAndUpdateImg,
    getCachedImg,
    removeCachedImg,
    replaceAndUpdateImg,
    hasGalleryImg,
    loading,
  };
};
